/**
 * Creates or refreshes the Convex mirror of the signed-in Clerk identity.
 *
 * Called once from the signed-in root. Returns a status so the shell can hold
 * off rendering data views until the user row exists — without it, every query
 * throws "No Dashbase user for this identity yet".
 */
import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { browserTimezone } from "@/lib/dates";

export type EnsureUserStatus = "pending" | "ready" | "error";

export function useEnsureUser(): EnsureUserStatus {
  const ensureUser = useMutation(api.users.ensureUser);
  const [status, setStatus] = useState<EnsureUserStatus>("pending");

  // `useMutation` returns a new function identity on some re-renders. Holding
  // it in a ref keeps it out of the effect's dependency array, which would
  // otherwise re-run the mutation in a loop.
  const ensureRef = useRef(ensureUser);
  ensureRef.current = ensureUser;

  useEffect(() => {
    let cancelled = false;

    ensureRef
      .current({ timezone: browserTimezone() })
      .then(() => {
        if (!cancelled) setStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("ensureUser failed", error);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}
